import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { getAuthHeaders } from '@/hooks/useAuth';
import {
  ChevronUp,
  ChevronDown,
  Link2,
  Unlink,
  Search,
  Calendar,
  Building2,
  Clock,
  AlertCircle,
  Loader2,
  ExternalLink,
  Shield,
} from 'lucide-react';

interface CNAuditionRequest {
  id: string;
  project: { id: number; name: string; castingCompany?: string };
  role: { id: number; name: string };
  status: string;
  note?: string;
  mediaTypes: string[];
  dueDate?: string;
  submissionId?: number;
}

interface CNStatus {
  connected: boolean;
  cnEmail?: string;
  cnAccountId?: number;
  cnArtistId?: number;
  cnRoles?: string[];
  connectedAt?: string;
}

// Casting Networks is gated on prod until the integration is fully fleshed
// out. Dev-post.heyjoe.io and localhost still get it for QA; anything served
// from the prod hostnames hides the section entirely. The gate is applied via
// a wrapper component so the inner implementation's hook order stays stable.
const PROD_HOSTNAMES = new Set(['post.heyjoe.io', 'plus.heyjoe.io']);
const isProdHost = (): boolean => {
  if (typeof window === 'undefined') return false;
  return PROD_HOSTNAMES.has(window.location.hostname);
};

export const CastingNetworksSection: React.FC = () => {
  if (isProdHost()) return null;
  return <CastingNetworksSectionInner />;
};

const CastingNetworksSectionInner: React.FC = () => {
  const { toast } = useToast();
  const [expanded, setExpanded] = useState(false);
  const [status, setStatus] = useState<CNStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [showConnect, setShowConnect] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [connectError, setConnectError] = useState('');

  // Audition requests state
  const [requests, setRequests] = useState<CNAuditionRequest[]>([]);
  const [allRequests, setAllRequests] = useState<CNAuditionRequest[]>([]);
  const [auditionsLoading, setAuditionsLoading] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [showExpired, setShowExpired] = useState(false);

  const headers = () => getAuthHeaders();

  // CN's mediaTypes are raw enums (IMAGE, AUDIO, VIDEO, ...). Render a
  // human-readable badge for non-video auditions so the user knows why
  // there's no Enhance button — print/photo/audio look like broken video
  // requests otherwise. Returns null for video (handled separately).
  const prettyMediaType = (types: string[] | undefined): string | null => {
    if (!types || types.length === 0) return 'Other';
    const nonVideo = types.filter(t => t !== 'VIDEO');
    if (nonVideo.length === 0) return null;
    return nonVideo
      .map(t => {
        const u = t.toUpperCase();
        if (u === 'IMAGE' || u === 'IMAGES' || u === 'PHOTO') return 'Print';
        if (u === 'AUDIO') return 'Audio';
        return t.charAt(0).toUpperCase() + t.slice(1).toLowerCase();
      })
      .join(' / ');
  };

  // CN status enums to friendlier labels (raw "MEDIA_ADDED" → "Submitted").
  const prettyStatus = (status: string | undefined): string => {
    if (!status) return '';
    const key = status.toUpperCase().replace(/[\s-]+/g, '_');
    const map: Record<string, string> = {
      MEDIA_ADDED: 'Submitted',
      PENDING: 'Pending',
      IN_PROGRESS: 'In progress',
      COMPLETED: 'Completed',
      EXPIRED: 'Expired',
      CONFIRMED: 'Confirmed',
      DECLINED: 'Declined',
      VIEWED: 'Viewed',
      NEW: 'New',
    };
    if (map[key]) return map[key];
    return status.toLowerCase().replace(/_/g, ' ').replace(/^./, c => c.toUpperCase());
  };

  const isExpired = (req: CNAuditionRequest): boolean =>
    !!(req.dueDate && new Date(req.dueDate).getTime() < Date.now());

  // Check connection status on mount
  useEffect(() => {
    checkStatus();
  }, []);

  const checkStatus = async () => {
    try {
      const res = await fetch('/api/cn/status', { headers: headers() });
      if (res.ok) {
        const data = await res.json();
        setStatus(data);
        if (data.connected && expanded) fetchAuditions(1);
      }
    } catch {}
  };

  const handleConnect = async () => {
    if (!email || !password) return;
    setConnecting(true);
    setConnectError('');

    try {
      const res = await fetch('/api/cn/connect', {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();

      if (!res.ok) {
        setConnectError(data.error || 'Connection failed');
        return;
      }

      setStatus({ connected: true, ...data });
      setShowConnect(false);
      setEmail('');
      setPassword('');
      toast({ title: 'Casting Networks connected', description: `Linked to ${data.cnEmail}` });
      fetchAuditions(1);
    } catch (err: any) {
      setConnectError(err.message || 'Connection failed');
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect your Casting Networks account?')) return;
    try {
      await fetch('/api/cn/disconnect', { method: 'POST', headers: headers() });
      setStatus({ connected: false });
      setAuditions([]);
      toast({ title: 'Casting Networks disconnected' });
    } catch {}
  };

  const fetchAuditions = useCallback(async () => {
    setAuditionsLoading(true);
    try {
      const res = await fetch('/api/cn/auditions', { headers: headers() });
      if (res.status === 401) {
        // Stored CN token expired and the auto-refresh failed — fall back to
        // the disconnected state so the UI prompts the user to reconnect.
        setStatus({ connected: false } as any);
        toast({
          title: 'Casting Networks session expired',
          description: 'Please reconnect your Casting Networks account.',
          variant: 'destructive',
        });
        return;
      }
      if (!res.ok) throw new Error('Failed to fetch auditions');
      const data = await res.json();
      setRequests(data.requests || []);
      setAllRequests(data.allRequests || []);
    } catch (err: any) {
      toast({ title: 'Failed to load auditions', description: err.message, variant: 'destructive' });
    } finally {
      setAuditionsLoading(false);
    }
  }, [toast]);

  const handleExpand = () => {
    const next = !expanded;
    setExpanded(next);
    if (next && status?.connected && requests.length === 0 && allRequests.length === 0) {
      fetchAuditions();
    }
  };

  // Remove unused Search import reference
  const formatDeadline = (dateStr: string) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diffHours = Math.round((d.getTime() - now.getTime()) / (1000 * 60 * 60));
    if (diffHours < 0) return 'Expired';
    if (diffHours < 24) return `${diffHours}h left`;
    const diffDays = Math.round(diffHours / 24);
    return `${diffDays}d left`;
  };

  return (
    <div className="mt-5">
      {/* Subtle collapsed row — plain text disclosure, no card chrome. The
          full card UI only appears once the row is expanded. */}
      <button
        type="button"
        onClick={handleExpand}
        className="flex items-center justify-between w-full text-left"
        style={{
          background: 'transparent',
          border: 0,
          padding: '6px 2px',
          cursor: 'pointer',
          fontFamily: 'inherit',
        }}
      >
        <div className="flex items-center gap-2 min-w-0">
          <Link2 className="w-3.5 h-3.5 shrink-0" style={{ color: 'var(--hj-ink-4)' }} />
          <span className="shrink-0" style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--hj-ink-3)' }}>
            Casting Networks
          </span>
          {status?.connected && (
            <span className="inline-block rounded-full shrink-0" style={{ width: 6, height: 6, background: 'var(--hj-green)' }} />
          )}
          <span className="truncate" style={{ fontSize: 11.5, color: 'var(--hj-ink-4)' }}>
            {status?.connected
              ? `Connected as ${status.cnEmail}`
              : 'Connect to see your self-tape requests'}
          </span>
        </div>
        {expanded
          ? <ChevronUp className="w-4 h-4 shrink-0" style={{ color: 'var(--hj-ink-4)' }} />
          : <ChevronDown className="w-4 h-4 shrink-0" style={{ color: 'var(--hj-ink-4)' }} />}
      </button>

      {expanded && (
        <Card className="mt-2 p-4 sm:p-6">
        <div className="space-y-4">
          {/* Not connected — show connect flow */}
          {!status?.connected && !showConnect && (
            <div className="text-center py-6 space-y-3">
              <Link2 className="w-10 h-10 mx-auto text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                Connect your Casting Networks account to browse auditions and submit enhanced tapes directly.
              </p>
              <Button onClick={() => setShowConnect(true)}>
                <Link2 className="w-4 h-4 mr-2" /> Connect Casting Networks
              </Button>
            </div>
          )}

          {/* Connect form */}
          {!status?.connected && showConnect && (
            <div className="border rounded-lg p-4 space-y-4">
              <div className="flex items-start gap-2 bg-blue-50 dark:bg-blue-950/30 p-3 rounded-lg text-xs text-blue-800 dark:text-blue-200">
                <Shield className="w-4 h-4 mt-0.5 shrink-0" />
                <div>
                  <p className="font-medium mb-1">Secure connection</p>
                  <p>Hey Joe will access your Casting Networks account on your behalf to display auditions and submit tapes. Your credentials are encrypted at rest. You can disconnect at any time.</p>
                </div>
              </div>

              {connectError && (
                <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  {connectError}
                </div>
              )}

              <div className="space-y-2">
                <Input
                  type="email"
                  placeholder="Casting Networks email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                />
                <Input
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleConnect()}
                />
              </div>

              <div className="flex gap-2">
                <Button onClick={handleConnect} disabled={connecting || !email || !password}>
                  {connecting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Connecting...</> : 'Connect'}
                </Button>
                <Button variant="outline" onClick={() => { setShowConnect(false); setConnectError(''); }}>
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* Connected — show auditions */}
          {status?.connected && (() => {
            const baseList = showAll ? allRequests : requests;
            const expiredCount = baseList.filter(isExpired).length;
            const displayed = showExpired ? baseList : baseList.filter(r => !isExpired(r));

            const headerText = (() => {
              if (auditionsLoading) return 'Loading...';
              if (showAll) {
                return `Showing all ${allRequests.length} audition${allRequests.length !== 1 ? 's' : ''}`;
              }
              if (requests.length > 0) {
                return `${requests.length} self-tape request${requests.length !== 1 ? 's' : ''}`;
              }
              if (allRequests.length > 0) return 'No active self-tape requests';
              return 'No audition requests';
            })();

            return (
              <>
                {/* Header with disconnect */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-wrap">
                    <p className="text-sm text-muted-foreground">{headerText}</p>
                    {allRequests.length > requests.length && (
                      <button
                        onClick={() => setShowAll(!showAll)}
                        className="text-xs text-primary hover:underline"
                      >
                        {showAll ? 'Show video only' : `Show all (${allRequests.length})`}
                      </button>
                    )}
                    {expiredCount > 0 && (
                      <button
                        onClick={() => setShowExpired(!showExpired)}
                        className="text-xs text-muted-foreground hover:underline"
                      >
                        {showExpired ? 'Hide expired' : `Show expired (${expiredCount})`}
                      </button>
                    )}
                  </div>
                  <Button variant="ghost" size="sm" className="text-xs text-muted-foreground" onClick={handleDisconnect}>
                    <Unlink className="w-3 h-3 mr-1" /> Disconnect
                  </Button>
                </div>

                {/* Audition requests */}
                {auditionsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <div className="space-y-2">
                    {displayed.length === 0 && (
                      <div className="text-center py-6 text-sm text-muted-foreground">
                        <p>No {showAll ? '' : 'self-tape '}requests from casting directors right now.</p>
                        <p className="text-xs mt-1">When a CD requests a self-tape, it'll show up here so you can enhance and submit it.</p>
                      </div>
                    )}
                    {displayed.map(req => {
                      const deadline = req.dueDate ? formatDeadline(req.dueDate) : null;
                      const expired = isExpired(req);
                      const hasVideo = (req.mediaTypes || []).includes('VIDEO');
                      const nonVideoLabel = hasVideo ? null : prettyMediaType(req.mediaTypes);
                      const company = req.project?.castingCompany?.replace(/"/g, '') || '';

                      return (
                        <div
                          key={req.id}
                          className={`border rounded-lg p-3 hover:border-primary/30 transition-colors ${expired ? 'opacity-50' : ''}`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-sm truncate">{req.role.name}</p>
                              <p className="text-xs text-muted-foreground truncate">{req.project.name}</p>
                              <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                                {hasVideo && (
                                  <span className="text-[10px] bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded font-medium">Self-Tape</span>
                                )}
                                {nonVideoLabel && (
                                  <span className="text-[10px] bg-muted text-muted-foreground px-1.5 py-0.5 rounded font-medium">{nonVideoLabel}</span>
                                )}
                                <span className="text-[10px] bg-secondary px-1.5 py-0.5 rounded">{prettyStatus(req.status)}</span>
                                {company && (
                                  <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
                                    <Building2 className="w-3 h-3" /> {company}
                                  </span>
                                )}
                                {deadline && (
                                  <span className={`flex items-center gap-1 text-[10px] ${expired ? 'text-red-500' : 'text-muted-foreground'}`}>
                                    <Clock className="w-3 h-3" /> {deadline}
                                  </span>
                                )}
                              </div>
                            </div>
                            {hasVideo && !expired && (
                              <Button size="sm" variant="outline" className="shrink-0 text-xs">
                                Enhance
                              </Button>
                            )}
                          </div>
                          {req.note && (
                            <p className="text-[11px] text-muted-foreground mt-2 line-clamp-2">{req.note}</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            );
          })()}
        </div>
        </Card>
      )}
    </div>
  );
};

export default CastingNetworksSection;
